import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Btn } from "@/components/UI";
import { StickyNote, ArrowRight } from "lucide-react";

/** Dashboard widget — "İşlem Bekleyen Toplantı Notları" (spec madde 84).
 *  Shows per-department counts of active notes on IN_PROGRESS meetings.
 *  Each row → deep link into /meetings with the right filter.
 */
const DEPT_META = [
  { key: "RND",          label: "Ar-Ge",           color: "violet" },
  { key: "SALES_ORDER",  label: "Satış Siparişi",  color: "emerald" },
  { key: "PURCHASING",   label: "Satın Alma",      color: "amber" },
  { key: "OTHER",        label: "Diğer",           color: "slate" },
];

export default function MeetingsSummaryCard() {
  const [counts, setCounts] = useState({ RND: 0, SALES_ORDER: 0, PURCHASING: 0, OTHER: 0 });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/meetings/pending-counts-all");
      setCounts(r || {});
    } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);
  const total = DEPT_META.reduce((s, d) => s + (counts[d.key] || 0), 0);
  return (
    <Card className="p-4" data-testid="meetings-summary-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <StickyNote size={14} className="text-amber-600 dark:text-amber-400" />
          İşlem Bekleyen Toplantı Notları
        </h3>
        <Link to="/meetings">
          <Btn size="sm" variant="secondary" data-testid="meetings-summary-goto">Tümü <ArrowRight size={12}/></Btn>
        </Link>
      </div>
      {loading && <div className="text-xs text-slate-400">Yükleniyor…</div>}
      {!loading && total === 0 && (
        <div className="text-xs text-slate-500 py-3 text-center italic">
          İşleme alınmış toplantı notu yok.
        </div>
      )}
      {!loading && total > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {DEPT_META.map(d => {
            const n = counts[d.key] || 0;
            if (n === 0) return null;
            return (
              <Link
                key={d.key}
                to={`/meetings?department=${d.key}&status=IN_PROGRESS`}
                data-testid={`meetings-summary-${d.key}`}
                className={`flex items-center justify-between px-3 py-2 rounded-md border border-${d.color}-200 bg-${d.color}-50 hover:bg-${d.color}-100 transition`}
              >
                <span className={`text-xs font-semibold text-${d.color}-800`}>{d.label}</span>
                <span className={`inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-full text-xs font-bold text-white bg-${d.color}-600`}>
                  {n} not
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}
